import React, { useEffect, useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Edit as EditIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { ExperienceOffer, TimeGroups } from '~/services/ExperiencesService';
import OffersService, { PackageTargeUpgradesMappingSource, PackageUpgradeTableRow } from '~/services/OffersService';

import { arrayToObject } from '~/utils/arrayUtils';
import { calculateExperienceCostsForPackage } from '~/utils/experienceUtils';
import { calculateIncrementalPackageCost, mergePackageUpgrades } from '~/utils/packageUpgradeUtils';

interface Props {
  offer: App.AccommodationOffer;
  experiencesTimes: Record<string, Array<TimeGroups>>;
  experiencesByPackageId: Record<string, Array<ExperienceOffer>>;
  isFetchingExperiencesTimes: boolean;
  onAvailabilityPackagesChange: (packageIds: Array<string>) => void;
}

// Default upgrade options
const upgradeTypeOptions = ['Package or room upgrade', 'Room type upgrade', 'Package type upgrade'];

const manualOrDefaultOptions = ['Manual', 'Default'];

export default function PackageUpgradePane({
  offer,
  experiencesTimes,
  experiencesByPackageId,
  isFetchingExperiencesTimes,
  onAvailabilityPackagesChange,
}: Props) {
  const [rows, setRows] = useState<Array<PackageUpgradeTableRow>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const appPackages = offer.packages || [];

  const experienceCosts = useMemo<Map<string, number>>(() => {
    const costs = appPackages.map((pkg: App.Package) => {
      const experiences = experiencesByPackageId[pkg.id_salesforce_external] ?? [];
      if (!experiences) {
        return [pkg.id_salesforce_external, 0];
      }
      const result = calculateExperienceCostsForPackage(pkg.id_salesforce_external, experiences, experiencesTimes);
      return [pkg.id_salesforce_external, result.costPriceAud];
    });
    return new Map(costs.map((cost) => [String(cost[0]), Number(cost[1])]));
  }, [appPackages, experiencesByPackageId, experiencesTimes]);

  const packageById = useMemo(() => {
    const packageById = arrayToObject(appPackages, (pkg: App.Package) => pkg.le_package_id);
    return packageById;
  }, [appPackages]);

  const columns: Array<GridColDef> = [
    {
      field: 'basePackageName',
      headerName: 'Base package name',
      flex: 1,
      editable: false, // Not editable
    },
    {
      field: 'targetPackageName',
      headerName: 'Target package name',
      flex: 1,
      minWidth: 200, // Ensure minimum width
    },
    {
      field: 'incrementalCostPercent',
      headerName: 'Incremental cost (%)',
      flex: 1,
      editable: false, // Not editable for now
      type: 'number',
      valueGetter: (value: any, row: any) => {
        if (!row || !row.targetPackageId || !row.basePackageId) {
          return 0;
        }

        const basePackage = packageById[row.basePackageId];
        const targetPackage = packageById[row.targetPackageId];

        if (basePackage && targetPackage) {
          const result = calculateIncrementalPackageCost({
            basePackage,
            targetPackage,
            experienceCosts,
          });
          return result.percentage;
        }

        return 0;
      },
      renderCell: ({ row, value }) => {
        if (!row?.basePackageId || !row?.targetPackageId) {
          return value;
        }

        return isFetchingExperiencesTimes ? <CircularProgress size={18} /> : value;
      },
    },
    {
      field: 'upgradeType',
      headerName: 'Upgrade type',
      flex: 1,
      editable: false, // Not editable
      type: 'singleSelect',
      valueOptions: upgradeTypeOptions,
    },
    {
      field: 'manualOrDefault',
      headerName: 'Manual or default',
      flex: 1,
      editable: false, // Not editable
      type: 'singleSelect',
      valueOptions: manualOrDefaultOptions,
    },
  ];

  // Helper function to map upgrade type from API to display format
  const mapUpgradeType = (apiUpgradeType: string): PackageUpgradeTableRow['upgradeType'] => {
    switch (apiUpgradeType?.toLowerCase()) {
      case 'room':
        return 'Room type upgrade';
      case 'package':
        return 'Package type upgrade';
      default:
        return 'Package or room upgrade';
    }
  };

  // Helper function to map mapping source to display format
  const mapMappingSource = (mappingSource: string): PackageTargeUpgradesMappingSource => {
    return mappingSource === 'manual' ? 'Manual' : 'Default';
  };

  useEffect(() => {
    const createDefaultRows = (): Array<PackageUpgradeTableRow> => {
      const defaultRows: Array<PackageUpgradeTableRow> = [];
      // Create 2 rows for each package (Room + Package upgrade)
      appPackages.forEach((pkg: App.Package, index: number) => {
        const pkgId = pkg.le_package_id || `pkg-${index}`;
        // Room type upgrade row
        defaultRows.push({
          id: `${pkgId}-room`,
          basePackageName: pkg.deal_option_name,
          targetPackageName: '',
          basePackageId: pkg.le_package_id,
          targetPackageId: '',
          incrementalCostPercent: 0,
          upgradeType: 'Room type upgrade',
          manualOrDefault: 'Default',
        });

        // Package type upgrade row
        defaultRows.push({
          id: `${pkgId}-package`,
          basePackageName: pkg.deal_option_name,
          targetPackageName: '',
          basePackageId: pkg.le_package_id,
          targetPackageId: '',
          incrementalCostPercent: 0,
          upgradeType: 'Package type upgrade',
          manualOrDefault: 'Default',
        });
      });

      return defaultRows;
    };

    const fetchAndMergeUpgrades = async () => {
      setIsLoading(true);
      try {
        // Create default empty rows
        let finalRows = createDefaultRows();

        if (offer?.id_salesforce_external) {
          const response = await OffersService.getPackageTargetUpgrades(offer.id_salesforce_external);

          if (response?.success && response?.result) {
            finalRows = mergePackageUpgrades(finalRows, response.result, {
              mapUpgradeType,
              mapMappingSource,
              packageById,
            });
          }
        }

        setRows(finalRows);
      } catch (error) {
        console.error('Error fetching package target upgrades:', error);
        // Still show default rows even if API fails
        setRows(createDefaultRows());
      } finally {
        setIsLoading(false);
      }
    };

    if (Object.keys(packageById).length > 0) {
      fetchAndMergeUpgrades();
    }
  }, [offer?.id_salesforce_external, packageById]);

  useEffect(() => {
    const packageIds = rows.reduce<Array<string>>((ids, row) => {
      if (!row.basePackageId || !row.targetPackageId) {
        return ids;
      }

      const basePackage = packageById[row.basePackageId];
      const targetPackage = packageById[row.targetPackageId];
      if (basePackage?.id_salesforce_external) {
        ids.push(basePackage.id_salesforce_external);
      }
      if (targetPackage?.id_salesforce_external) {
        ids.push(targetPackage.id_salesforce_external);
      }
      return ids;
    }, []);

    onAvailabilityPackagesChange([...new Set(packageIds)]);
  }, [onAvailabilityPackagesChange, packageById, rows]);

  const handleRowUpdate = (newRow: PackageUpgradeTableRow) => {
    setRows(rows.map((row) => (row.id === newRow.id ? newRow : row)));
    return newRow;
  };

  const handleRowUpdateError = (error: Error) => {
    console.error('Error updating row:', error);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '15rem',
          maxHeight: '25rem',
          width: '100%',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Loading Package Upgrades...</Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Package Upgrades</Typography>
        <Link to={`/edit-offers/${offer.id_salesforce_external}/packages`}>
          <IconButton size="medium">
            <EditIcon fontSize="medium" />
          </IconButton>
        </Link>
      </Box>
      <Paper sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleRowUpdateError}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}
